import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { aiApi } from '../../api/ai';
import toast from 'react-hot-toast';
import { Sparkles, Loader2 } from 'lucide-react';

interface AIPanelProps {
  ticketId: string;
  ticketTitle: string;
  ticketDescription: string;
  onApplySuggestion?: (text: string) => void;
}

export default function AIPanel({
  ticketId,
  ticketTitle,
  ticketDescription,
  onApplySuggestion,
}: AIPanelProps) {
  const [summary, setSummary] = useState<string | null>(null);
  const [suggestedReply, setSuggestedReply] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'reply' | 'kb'>('summary');

  const summarizeMutation = useMutation({
    mutationFn: () => aiApi.summarize(ticketId),
    onSuccess: (s) => setSummary(s),
    onError: (e: Error) => toast.error(e.message),
  });

  const suggestReplyMutation = useMutation({
    mutationFn: () => aiApi.suggestReply(ticketId),
    onSuccess: (s) => setSuggestedReply(s),
    onError: (e: Error) => toast.error(e.message),
  });

  const kbMatchMutation = useMutation({
    mutationFn: () => aiApi.kbMatch(`${ticketTitle}\n${ticketDescription}`),
    onSuccess: (matches) => {
      if (matches?.length) {
        setKbMatches(matches);
        setActiveTab('kb');
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [kbMatches, setKbMatches] = useState<any[]>([]);

  useEffect(() => {
    if ((ticketTitle || ticketDescription) && ticketId) {
      kbMatchMutation.mutate();
    }
  }, [ticketId]);

  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-primary" />
        <h3 className="font-medium text-fg">AI Assistant</h3>
      </div>

      <div className="flex gap-2 mb-3">
        <button
          className={`btn btn-ghost text-xs ${activeTab === 'summary' ? 'bg-primary-muted text-primary' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </button>
        <button
          className={`btn btn-ghost text-xs ${activeTab === 'reply' ? 'bg-primary-muted text-primary' : ''}`}
          onClick={() => setActiveTab('reply')}
        >
          Suggest Reply
        </button>
        <button
          className={`btn btn-ghost text-xs ${activeTab === 'kb' ? 'bg-primary-muted text-primary' : ''}`}
          onClick={() => setActiveTab('kb')}
        >
          KB Match
        </button>
      </div>

      <div>
        {activeTab === 'summary' && (
          <div>
            {!summary ? (
              <button
                className="btn btn-secondary w-full text-sm"
                onClick={() => summarizeMutation.mutate()}
                disabled={summarizeMutation.isPending}
              >
                {summarizeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Generate summary'}
              </button>
            ) : (
              <p className="text-sm text-muted whitespace-pre-wrap">{summary}</p>
            )}
          </div>
        )}

        {activeTab === 'reply' && (
          <div>
            {!suggestedReply ? (
              <button
                className="btn btn-secondary w-full text-sm"
                onClick={() => suggestReplyMutation.mutate()}
                disabled={suggestReplyMutation.isPending}
              >
                {suggestReplyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Suggest reply'}
              </button>
            ) : (
              <div>
                <p className="text-sm text-muted whitespace-pre-wrap mb-2">{suggestedReply}</p>
                {onApplySuggestion && (
                  <button
                    className="btn btn-primary text-sm"
                    onClick={() => onApplySuggestion(suggestedReply)}
                  >
                    Use as reply
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'kb' && (
          <div>
            {kbMatches.length === 0 ? (
              <button
                className="btn btn-secondary w-full text-sm"
                onClick={() => kbMatchMutation.mutate()}
                disabled={kbMatchMutation.isPending}
              >
                {kbMatchMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Find matching articles'}
              </button>
            ) : (
              <div className="space-y-2">
                {kbMatches.map((m: any, i: number) => (
                  <a
                    key={i}
                    href={`/kb/${m.slug ?? m.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-primary hover:underline"
                  >
                    {m.title}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
